import { drawFrame, mapToPlot, drawSpatialFieldRgb } from './canvasUtils';

function gaussianNoise() {
    let u = 0;
    let v = 0;
    while (u === 0) u = Math.random();
    while (v === 0) v = Math.random();
    return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
}

function computeTrailBounds(trail, padding = 0.08) {
    if (!trail.length) {
        return { xMin: 0, xMax: 20, yMin: 0, yMax: 20 };
    }
    let xMin = trail[0].x;
    let xMax = trail[0].x;
    let yMin = trail[0].y;
    let yMax = trail[0].y;
    for (let i = 1; i < trail.length; i++) {
        const p = trail[i];
        if (p.x < xMin) xMin = p.x;
        if (p.x > xMax) xMax = p.x;
        if (p.y < yMin) yMin = p.y;
        if (p.y > yMax) yMax = p.y;
    }
    const xPad = Math.max((xMax - xMin) * padding, 0.5);
    const yPad = Math.max((yMax - yMin) * padding, 0.5);
    return {
        xMin: Math.max(0, xMin - xPad),
        xMax: xMax + xPad,
        yMin: Math.max(0, yMin - yPad),
        yMax: yMax + yPad,
    };
}

function drawPhasePortrait(ctx, w, h, state, opts) {
    const { title, color, showMarker = true } = opts;
    const bounds = computeTrailBounds(state.trail);
    const area = drawFrame(ctx, w, h, {
        xLabel: 'prey (x)',
        yLabel: 'predator (y)',
        title,
        t: state.t,
    });

    if (state.trail.length < 2) return area;

    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    state.trail.forEach((p, i) => {
        const mapped = mapToPlot(area, p.x, p.y, bounds.xMin, bounds.xMax, bounds.yMin, bounds.yMax);
        if (i === 0) ctx.moveTo(mapped.x, mapped.y);
        else ctx.lineTo(mapped.x, mapped.y);
    });
    ctx.stroke();

    if (showMarker) {
        const last = mapToPlot(
            area,
            state.x,
            state.y,
            bounds.xMin,
            bounds.xMax,
            bounds.yMin,
            bounds.yMax
        );
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(last.x, last.y, 4, 0, Math.PI * 2);
        ctx.fill();
    }

    ctx.fillStyle = '#a1a1aa';
    ctx.font = '11px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(`points: ${state.trail.length}`, area.left, area.bottom + 28);

    return area;
}

function appendTrail(state, x, y) {
    state.x = x;
    state.y = y;
    state.trail.push({ x, y });
}

export const lotkaVolterra1D = {
    init: (w, h, params) => {
        const x0 = params.prey0;
        const y0 = params.predator0;
        return { x: x0, y: y0, t: 0, trail: [{ x: x0, y: y0 }] };
    },
    step: (ctx, w, h, state, params) => {
        const dt = 0.05;
        const { alpha, beta, delta, gamma } = params;
        const dx = alpha * state.x - beta * state.x * state.y;
        const dy = delta * state.x * state.y - gamma * state.y;
        const nx = Math.max(0, state.x + dx * dt);
        const ny = Math.max(0, state.y + dy * dt);
        state.t += dt;
        appendTrail(state, nx, ny);

        drawPhasePortrait(ctx, w, h, state, {
            title: 'ODE phase portrait (full trajectory)',
            color: '#2cbb5d',
        });
    },
};

export const lotkaVolterra2D = {
    init: (w, h) => {
        const n = 90;
        const prey = new Float32Array(n * n);
        const predator = new Float32Array(n * n);
        for (let i = 0; i < n * n; i++) {
            prey[i] = 1 + 0.3 * Math.random();
            predator[i] = 0.3 * Math.random();
        }
        for (let k = 0; k < 6; k++) {
            const cx = Math.floor(Math.random() * n);
            const cy = Math.floor(Math.random() * n);
            for (let dy = -4; dy <= 4; dy++) {
                for (let dx = -4; dx <= 4; dx++) {
                    const xx = cx + dx;
                    const yy = cy + dy;
                    if (xx >= 0 && xx < n && yy >= 0 && yy < n && dx * dx + dy * dy < 16) {
                        predator[yy * n + xx] += 1.2;
                    }
                }
            }
        }
        return { n, prey, predator, t: 0 };
    },
    step: (ctx, w, h, state, params) => {
        const { n, prey, predator } = state;
        const newPrey = new Float32Array(n * n);
        const newPredator = new Float32Array(n * n);
        const D = params.diffusion2d;
        const { alpha, beta, delta, gamma } = params;

        for (let y = 0; y < n; y++) {
            for (let x = 0; x < n; x++) {
                const idx = y * n + x;
                const lap =
                    prey[y * n + ((x - 1 + n) % n)] +
                    prey[y * n + ((x + 1) % n)] +
                    prey[((y - 1 + n) % n) * n + x] +
                    prey[((y + 1) % n) * n + x] -
                    4 * prey[idx];
                const lapP =
                    predator[y * n + ((x - 1 + n) % n)] +
                    predator[y * n + ((x + 1) % n)] +
                    predator[((y - 1 + n) % n) * n + x] +
                    predator[((y + 1) % n) * n + x] -
                    4 * predator[idx];

                const px = prey[idx];
                const py = predator[idx];
                newPrey[idx] = Math.max(0, px + 0.08 * (alpha * px - beta * px * py + D * lap));
                newPredator[idx] = Math.max(0, py + 0.08 * (delta * px * py - gamma * py + D * lapP));
            }
        }
        state.prey = newPrey;
        state.predator = newPredator;
        state.t += 0.08;

        drawSpatialFieldRgb(ctx, w, h, n, (idx) => {
            const pv = Math.min(1, newPrey[idx] / 3);
            const qv = Math.min(1, newPredator[idx] / 3);
            const g = Math.floor(pv * 200);
            const r = Math.floor(qv * 220);
            return `rgb(${r},${g + 30},60)`;
        }, {
            xLabel: 'x (space)',
            yLabel: 'y (space)',
            title: 'Prey (green) / Predator (red)',
            t: state.t,
        });
    },
};

// Stochastic Lotka-Volterra via multiplicative-noise SDE (Euler-Maruyama).
export const lotkaVolterraStochastic = {
    init: (w, h, params) => {
        const x0 = params.prey0;
        const y0 = params.predator0;
        return { x: x0, y: y0, t: 0, trail: [{ x: x0, y: y0 }] };
    },
    step: (ctx, w, h, state, params) => {
        const dt = 0.02;
        const sqrtDt = Math.sqrt(dt);
        const { alpha, beta, delta, gamma } = params;
        const sigma1 = params.noisePrey ?? 0.12;
        const sigma2 = params.noisePredator ?? 0.12;

        const driftX = alpha * state.x - beta * state.x * state.y;
        const driftY = delta * state.x * state.y - gamma * state.y;

        const diffX = sigma1 * state.x * gaussianNoise() * sqrtDt;
        const diffY = sigma2 * state.y * gaussianNoise() * sqrtDt;

        const nx = Math.max(0, state.x + driftX * dt + diffX);
        const ny = Math.max(0, state.y + driftY * dt + diffY);
        state.t += dt;
        appendTrail(state, nx, ny);

        drawPhasePortrait(ctx, w, h, state, {
            title: 'SDE phase portrait (full trajectory)',
            color: '#f9a602',
        });
    },
};
